import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { LocalStorage } from '@isophi/core-legacy';
import { BaseComponent, ToastOptions, ToastService } from '@isophi/core-ui';
import { KindergartenUsersService, TeacherInvitationsService, UserWithInvitation } from '@isophi/mng-api';
import { PopUpComponent } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  map,
  merge,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { UserService } from '../../../../core/services/user.service';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';

enum UsersSpecialFilters {
  NAME = 'name',
  EMAIL = 'email',
  ROLE = 'role',
  INVITED = 'invited',
  LAST_LOGIN = 'lastLogin',
}

type UsersSpecialFiltersValues = `${UsersSpecialFilters}`;

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  host: {
    class: 'page-layout',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UsersComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly helperService = inject(HelperService);
  private readonly storage = new LocalStorage();
  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly kindergartenUsersService = inject(KindergartenUsersService);
  private readonly teacherInvitationsService = inject(TeacherInvitationsService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly popUp = inject(PopUpComponent);
  private readonly userService = inject(UserService);
  protected readonly links = inject(RouterLinkFactory);

  protected readonly usersForm = this.formBuilder.group({
    search: new FormControl<string>(''),
    expired: new FormControl<boolean>(false),
  });

  protected readonly toastsOptions: ToastOptions = {
    delay: 15000,
    autohide: true,
    closeButton: true,
  };

  readonly reloadUsers$ = new BehaviorSubject<void>(undefined);
  protected readonly users$ = this.reloadUsers$.pipe(
    tap(() => {
      this.spinner.show();
    }),
    switchMap(() =>
      this.kindergartenUsersService.kindergartenUsersListList(true).pipe(
        switchMap((users) =>
          merge(
            this.usersForm.valueChanges.pipe(
              debounceTime(300),
              map(() => {
                return this.filterUsers(users);
              })
            ),
            this.ordering$.pipe(
              map(() => {
                return this.filterUsers(users);
              })
            )
          ).pipe(startWith(this.filterUsers(users)))
        ),
        tap(() => {
          this.spinner.hide();
        }),
        catchError((errorResponse) => {
          this.spinner.hide();
          const error = errorResponse.error?.detail;
          this.toastService.error(error || this.translocoService.translate('users/usersLoadingFailed'));
          return of(null);
        })
      )
    ),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  protected readonly specialFilters = UsersSpecialFilters;
  protected ordering: UsersSpecialFiltersValues[] | string[] = [];
  protected ordering$ = new ReplaySubject<UsersSpecialFiltersValues[] | string[]>();

  ngOnInit() {
    this.helperService.setTitle(null, this.translocoService.translate('user/title'));
    this.reloadUsers$.pipe(
      switchMap(() =>
        this.userService.teachers$.pipe(
          tap(() => {
            this.helperService.setSmallTextTitle(`${this.userService.usersCount}`);
          })
        )
      ),
      takeUntil(this.ngUnsubscribe$)
    );

    if (this.storage.hasItem(this.lSConstService.usersFilter)) {
      const storageData = this.storage.getItem(this.lSConstService.usersFilter);
      if (storageData && storageData !== 'undefined') {
        this.usersForm.patchValue(JSON.parse(storageData));
      }
    }

    this.usersForm.controls.expired.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((newValue) => {
      this.storage.setItem(this.lSConstService.usersFilter, JSON.stringify({ expired: newValue }));
    });
  }

  private filterUsers(users: UserWithInvitation[]): UserWithInvitation[] {
    let filtered = users;

    const searchTerm = this.usersForm.controls.search.value?.toLowerCase();
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm) ||
          user.email.toLowerCase().includes(searchTerm) ||
          user.role.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by expiration
    if (this.usersForm.controls.expired.value) {
      filtered = filtered.filter((user) => !user.isUser && user.isExpired);
    }

    // grab first filter cause app-sortable component works that way.....
    const specialFiltering: UsersSpecialFiltersValues | string = this.ordering.at(0);

    return filtered.sort((a, b) => {
      // Use special filtering (bypassing defaults)
      if (specialFiltering) {
        const isDate = specialFiltering.includes(UsersSpecialFilters.INVITED) || specialFiltering.includes(UsersSpecialFilters.LAST_LOGIN);
        if (specialFiltering.startsWith('-')) {
          const filterKey = specialFiltering.substring(1);
          return isDate
            ? new Date(b[filterKey]).getTime() - new Date(a[filterKey]).getTime()
            : b[filterKey]?.localeCompare(a[filterKey], 'cs');
        }
        return isDate
          ? new Date(a[specialFiltering]).getTime() - new Date(b[specialFiltering]).getTime()
          : a[specialFiltering]?.localeCompare(b[specialFiltering], 'cs');
      }

      // Active users come before invitations
      if (a.isUser && !b.isUser) return -1;
      if (!a.isUser && b.isUser) return 1;

      // Both are active users - sort alphabetically by selected filter
      if (a.isUser && b.isUser) {
        return a.name.localeCompare(b.name, 'cs');
      }

      // Both are invitations - sort by invited date (newest first)
      const dateA = a.invited ? new Date(a.invited).getTime() : 0;
      const dateB = b.invited ? new Date(b.invited).getTime() : 0;
      return dateB - dateA;
    });
  }

  protected reloadUsers(): void {
    this.reloadUsers$.next();
  }

  protected onResendInvitation(invitationId: string): void {
    this.spinner.show();
    this.teacherInvitationsService
      .teacherInvitationsResendCreate(invitationId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate('users/invitationsResendSuccess'), undefined, this.toastsOptions);
          // Reload the list to show updated data
          this.reloadUsers$.next();
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('users/invitationsResendError'), undefined, this.toastsOptions);
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  protected async onDeleteInvitation(invitationId: string) {
    const title = this.translocoService.translate('users/deleteInvitation');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    this.spinner.show();

    this.teacherInvitationsService
      .teacherInvitationsDestroy(invitationId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate('users/invitationsDeleteSuccess'), undefined, this.toastsOptions);
          // Reload the list to show updated data
          this.reloadUsers$.next();
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('users/invitationsDeleteError'), undefined, this.toastsOptions);
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  protected onOrderingChange() {
    this.ordering$.next(this.ordering);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.helperService.clearSmallTextTitle();
  }
}
